import { DialogBoundary, DialogTrigger } from "@suse-o11y/dialog";
import type { IdOrIdentifier } from "@suse-o11y/id-or-identifier";
import { useComputed } from "@suse-o11y/signal";
import React from "react";
import { useSelector } from "react-redux";

import { selectTimeRange } from "../../../../app/state/view/timeline/TimelineSelectors";
import { Api } from "../../../../common/openapi";
import { useUserProfile } from "../../../../common/use-user-profile";
import { DashboardVariablesDrawer } from "../../../../components/_dashboard-variables-drawer";
import { DashboardYamlDrawer } from "../../../../components/_dashboard-yaml-drawer";
import { SetupDashboardDrawer } from "../../../../components/_setup-dashboard-drawer";
import { type SaveAsButtonAction, SaveButton } from "../../../../components/react/save/SaveButton";
import { type DashboardReadFullSchema, queryFullDashboard } from "../../../../queries/dashboards";
import { useQuery } from "../../../../queries/react-query";
import { useDashboardsPermissions } from "../../permissions";
import { useDashboardContext } from "../_dashboard-context";
import { dashboardFormOutputToSpec } from "../_dashboard-form-state";
import type { DashboardState } from "../_dashboard-state";
import { useSaveDashboard } from "../_use-save-dashboard";

import { useDeleteDashboard } from "./_use-delete-dashboard";

function useDashboardSchema(idOrIdentifier: IdOrIdentifier, state: DashboardState) {
	const { data: dashboardSchema } = useQuery(queryFullDashboard(idOrIdentifier));

	return useComputed(
		(monitor) => {
			const output = state.form.getOutput(monitor);

			if (dashboardSchema == null || output == null) {
				return undefined;
			}

			return {
				...dashboardSchema,
				spec: dashboardFormOutputToSpec(output),
			};
		},
		[state, dashboardSchema],
	);
}

export interface DashboardSaveButtonProps {
	idOrIdentifier: IdOrIdentifier;
}

export const DashboardSaveButton: React.FC<DashboardSaveButtonProps> = ({ idOrIdentifier }) => {
	const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
	const [isSaveAsDrawerOpen, setIsSaveAsDrawerOpen] = React.useState(false);
	const [isVariablesDrawerOpen, setIsVariablesDrawerOpen] = React.useState(false);
	const [isEditDrawerOpen, setIsEditDrawerOpen] = React.useState(false);
	const [isYamlDrawerOpen, setIsYamlDrawerOpen] = React.useState(false);

	const timeRange = useSelector(selectTimeRange);
	const { state } = useDashboardContext();
	const [userProfile] = useUserProfile();

	const isDashboardDirty = useComputed((monitor) => state.form.isDirty(monitor), [state]);
	const schema = useDashboardSchema(idOrIdentifier, state);

	const saveDashboard = useSaveDashboard();

	const deleteDashboard = useDeleteDashboard({
		onConfirm: () => {
			// clear the state after successful deletion
			state.clear(idOrIdentifier);
		},
	});

	const { hasCreateDashboardsPermission, hasUpdateDashboardsPermission, hasDeleteDashboardsPermission } =
		useDashboardsPermissions().getOrElse({
			hasCreateDashboardsPermission: false,
			hasUpdateDashboardsPermission: () => false,
			hasDeleteDashboardsPermission: () => false,
		});

	return (
		<SaveButton
			isDropdownOpen={isDropdownOpen}
			onDropdownOpen={setIsDropdownOpen}
			reset={{
				enabler: isDashboardDirty,
				title: "Reset dashboard",
				isPrimary: true,
				onSelect: () => {
					state.form.reset();
				},
			}}
			rest={[
				{
					enabler: schema,
					title: "Edit dashboard variables",
					active: isVariablesDrawerOpen,
					onSelect: () => {
						setIsVariablesDrawerOpen(true);
					},
					trigger: ({ spec }, children) => (
						<DialogBoundary onOpenChange={setIsVariablesDrawerOpen} open={isVariablesDrawerOpen}>
							<DialogTrigger asChild>{children}</DialogTrigger>

							<DashboardVariablesDrawer
								initial={spec.variables}
								onSave={(variables) => {
									state.form.fields.variables.setInput(variables);
								}}
								timeRange={timeRange}
							/>
						</DialogBoundary>
					),
				} satisfies SaveAsButtonAction<DashboardReadFullSchema>,
				{
					enabler: schema,
					title: "Edit dashboard properties",
					active: isEditDrawerOpen,
					onSelect: () => {
						setIsEditDrawerOpen(true);
					},
					trigger: ({ id, ownerId, name, description, scope }, children) => (
						<DialogBoundary onOpenChange={setIsEditDrawerOpen} open={isEditDrawerOpen}>
							<DialogTrigger asChild>{children}</DialogTrigger>

							<SetupDashboardDrawer
								id={id}
								initial={{
									name,
									description,
									starred: userProfile?.starredDashboards.has(id) ?? false,
									publiclyAccessible: scope === Api.DashboardScope.PublicDashboard,
								}}
								readonly={!hasUpdateDashboardsPermission({ name, ownerId })}
								type="edit"
							/>
						</DialogBoundary>
					),
				} satisfies SaveAsButtonAction<DashboardReadFullSchema>,
				{
					enabler: schema,
					title: "Edit dashboard YAML",
					active: isYamlDrawerOpen,
					onSelect: () => {
						setIsYamlDrawerOpen(true);
					},
					trigger: ({ spec }, children) => (
						<DialogBoundary onOpenChange={setIsYamlDrawerOpen} open={isYamlDrawerOpen}>
							<DialogTrigger asChild>{children}</DialogTrigger>

							<DashboardYamlDrawer
								initial={spec}
								onSave={(next) => {
									state.form.setInput({
										panels: next.panels,
										variables: next.variables,
										settings: {
											refreshInterval: next.refreshInterval,
											timeRange: next.timeRange,
											saveVariables: next.saveVariables,
										},
									});
								}}
							/>
						</DialogBoundary>
					),
				} satisfies SaveAsButtonAction<DashboardReadFullSchema>,
				{
					enabler: schema != null && hasDeleteDashboardsPermission(schema) && schema,
					title: "Delete dashboard",
					onSelect: ({ id, name }) => {
						deleteDashboard(id, name);
					},
				} satisfies SaveAsButtonAction<DashboardReadFullSchema>,
			]}
			save={
				{
					enabler: isDashboardDirty && schema != null && hasUpdateDashboardsPermission(schema) && schema,
					title: "Save dashboard",
					isPrimary: true,
					onSelect: ({ spec }) => {
						saveDashboard({ idOrIdentifier, revision: spec });
					},
				} satisfies SaveAsButtonAction<DashboardReadFullSchema>
			}
			saveAs={
				{
					enabler: hasCreateDashboardsPermission && schema,
					title: "Save dashboard as...",
					isPrimary: isDashboardDirty,
					active: isSaveAsDrawerOpen,
					onSelect: () => {
						setIsSaveAsDrawerOpen(true);
					},
					trigger: ({ id, name, description, spec }, children) => (
						<DialogBoundary onOpenChange={setIsSaveAsDrawerOpen} open={isSaveAsDrawerOpen}>
							<DialogTrigger asChild>{children}</DialogTrigger>

							<SetupDashboardDrawer
								id={id}
								initial={{ name, description, starred: false, publiclyAccessible: false }}
								spec={spec}
								type="clone"
							/>
						</DialogBoundary>
					),
				} satisfies SaveAsButtonAction<DashboardReadFullSchema>
			}
		/>
	);
};
